import { useCallback, useEffect, useState } from 'react'
import { Gift as GiftIcon, Plus, Gavel } from 'lucide-react'
import { useT } from '@/i18n'
import { PageHeader } from '@/shell/AppShell'
import { DataTable } from '@/components/DataTable'
import type { AdminColumn, RowAction } from '@/components/DataTable/types'
import { Button } from '@/components/ui/button'
import { StatusBadge } from '@/components/StatusBadge'
import { PERMISSIONS } from '@/lib/permissions'
import { AddGiftDialog } from './AddGiftDialog'
import { LaunchAuctionDialog } from './LaunchAuctionDialog'
import { fetchGifts, fetchAuctions, type AdminGiftRow, type AdminGiftAuction } from './giftApi'

function formatDate(unixSeconds: number): string {
  return new Date(unixSeconds * 1000).toLocaleString()
}

/**
 * G1 — Star Gift catalog + auctions. Not part of the original 614-screen TZ scaffold
 * (src/lib/screens.ts) - gifts/auctions didn't exist as a real backend feature when that scaffold
 * was generated. Backend: MyTelegram.AdminServer's GiftsController (real, live).
 */
export function GiftsScreen() {
  const t = useT()
  const [addOpen, setAddOpen] = useState(false)
  const [auctionGift, setAuctionGift] = useState<AdminGiftRow | null>(null)
  const [refreshKey, setRefreshKey] = useState(0)
  const [auctions, setAuctions] = useState<AdminGiftAuction[]>([])
  const [auctionsLoading, setAuctionsLoading] = useState(true)

  const loadAuctions = useCallback(() => {
    setAuctionsLoading(true)
    fetchAuctions()
      .then(setAuctions)
      .catch(() => {})
      .finally(() => setAuctionsLoading(false))
  }, [])

  useEffect(() => {
    loadAuctions()
  }, [loadAuctions, refreshKey])

  const columns: AdminColumn<AdminGiftRow>[] = [
    { key: 'title', header: t.col.title, type: 'text', minWidth: 160 },
    { key: 'gift_id', header: t.col.id, type: 'id', width: 90 },
    { key: 'stars', header: t.gifts.stars, type: 'number', width: 90 },
    { key: 'upgrade_stars', header: t.gifts.upgradeStars, type: 'number', width: 110 },
    {
      key: 'limited',
      header: t.gifts.limited,
      type: 'text',
      width: 130,
      render: (r) => (r.limited ? `${r.availability_remains ?? 0}/${r.availability_total ?? 0}` : '—'),
    },
    { key: 'model_count', header: t.gifts.modelCount, type: 'number', width: 90 },
    {
      key: 'has_active_auction',
      header: t.gifts.activeAuction,
      type: 'bool',
      width: 110,
      render: (r) => (r.has_active_auction ? <StatusBadge status="active" label={t.gifts.statusActive} /> : '—'),
    },
  ]

  const rowActions: RowAction<AdminGiftRow>[] = [
    {
      id: 'launch-auction',
      label: t.gifts.launchAuction,
      icon: <Gavel size={14} />,
      permission: PERMISSIONS.USERS_VIEW,
      visible: (r) => !r.has_active_auction,
      onSelect: (r) => setAuctionGift(r),
    },
  ]

  return (
    <>
      <PageHeader
        title={t.nav.gifts}
        screenId="G1.01"
        verified
        actions={
          <Button variant="primary" size="sm" onClick={() => setAddOpen(true)}>
            <Plus size={14} />
            {t.gifts.addGift}
          </Button>
        }
      />

      <DataTable<AdminGiftRow>
        key={refreshKey}
        screenId="G1.01"
        permission={PERMISSIONS.USERS_VIEW}
        columns={columns}
        fetcher={fetchGifts}
        rowActions={rowActions}
        rowId={(r) => String(r.gift_id)}
        exportable
        emptyState={{
          icon: <GiftIcon size={28} strokeWidth={1.5} />,
          title: t.gifts.emptyTitle,
          description: t.gifts.emptyDesc,
        }}
      />

      <div className="border-t border-border-default px-3 py-2">
        <h2 className="mb-2 text-sm font-semibold text-primary">{t.gifts.auctions}</h2>
        {!auctionsLoading && auctions.length === 0 && (
          <p className="text-sm text-secondary">{t.gifts.auctionsEmptyDesc}</p>
        )}
        {auctions.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs text-tertiary">
                  <th className="py-1 pr-4 font-medium">{t.col.title}</th>
                  <th className="py-1 pr-4 font-medium">{t.gifts.status}</th>
                  <th className="py-1 pr-4 font-medium">{t.gifts.minBidAmount}</th>
                  <th className="py-1 pr-4 font-medium">{t.gifts.topBid}</th>
                  <th className="py-1 pr-4 font-medium">{t.gifts.giftsLeft}</th>
                  <th className="py-1 pr-4 font-medium">{t.gifts.endsAt}</th>
                </tr>
              </thead>
              <tbody>
                {auctions.map((a) => (
                  <tr key={a.gift_id} className="border-t border-border-subtle">
                    <td className="py-1.5 pr-4 text-primary">{a.gift_title}</td>
                    <td className="py-1.5 pr-4">
                      <StatusBadge
                        status={a.status === 'active' ? 'active' : 'expired'}
                        label={a.status === 'active' ? t.gifts.statusActive : t.gifts.statusFinished}
                      />
                    </td>
                    <td className="py-1.5 pr-4">{a.min_bid_amount}</td>
                    <td className="py-1.5 pr-4">{a.top_bid_amount || '—'}</td>
                    <td className="py-1.5 pr-4">
                      {a.gifts_left}/{a.gifts_available}
                    </td>
                    <td className="py-1.5 pr-4 text-secondary">{formatDate(a.end_date)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <AddGiftDialog
        open={addOpen}
        onClose={() => setAddOpen(false)}
        onCreated={() => setRefreshKey((k) => k + 1)}
      />
      <LaunchAuctionDialog
        gift={auctionGift}
        onClose={() => setAuctionGift(null)}
        onLaunched={() => setRefreshKey((k) => k + 1)}
      />
    </>
  )
}
